import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { FxRate } from '../api/types';

const STORAGE_KEY = 'cs2erp.currency';
const REFRESH_MS = 15 * 60 * 1000;

export type Currency = 'CNY' | 'BRL';

interface CurrencyValue {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  /** Cotação CNY→BRL vinda da Wise; null enquanto carrega ou se falhou. */
  rate: FxRate | null;
  /** Formata um valor **em yuan** na moeda escolhida. */
  money: (valueInCny: number | null | undefined) => string;
  /** Valor convertido, sem formatação. */
  convert: (valueInCny: number | null | undefined) => number | null;
  /** Sempre em yuan, para mostrar o valor original ao lado. */
  moneyCny: (value: number | null | undefined) => string;
  /** true quando o usuário pediu BRL mas ainda não há cotação. */
  unavailable: boolean;
}

const CurrencyContext = createContext<CurrencyValue | null>(null);

function detect(): Currency {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  return stored === 'BRL' || stored === 'CNY' ? stored : 'CNY';
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(detect);
  const [rate, setRate] = useState<FxRate | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currency);
  }, [currency]);

  // A cotação é buscada sempre: o rodapé mostra a taxa mesmo em modo yuan.
  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .fx()
        .then((r) => {
          if (alive) setRate(r);
        })
        .catch(() => {
          if (alive) setRate(null);
        });
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const convert = useCallback(
    (valueInCny: number | null | undefined): number | null => {
      if (valueInCny === null || valueInCny === undefined || !Number.isFinite(valueInCny)) return null;
      if (currency === 'CNY') return valueInCny;
      if (!rate) return null;
      return valueInCny * rate.rate;
    },
    [currency, rate],
  );

  /**
   * O dinheiro é sempre escrito à brasileira, mesmo com o site em inglês.
   *
   * Antes o formatador seguia o idioma da interface, e trocar para EN virava
   * "R$8.02" no lugar de "R$ 8,02" — o mesmo preço com outra pontuação. Preço
   * é o número que o cliente confere contra o que vai pagar, e ele paga em
   * reais independentemente da língua em que está lendo a página. Todo o resto
   * do site já fixava pt-BR; este era o único ponto fora.
   */
  const format = useCallback(
    (value: number | null, target: Currency): string => {
      if (value === null) return '—';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: target,
        // Sem isto o pt-BR escreve "CN¥" em vez do "¥" que o mercado usa.
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    [],
  );

  const value = useMemo<CurrencyValue>(
    () => ({
      currency,
      setCurrency: setCurrencyState,
      rate,
      convert,
      money: (valueInCny) => {
        const converted = convert(valueInCny);
        // Sem cotação, mostrar o yuan é melhor que esconder o preço.
        if (converted === null && currency === 'BRL' && valueInCny !== null && valueInCny !== undefined) {
          return format(valueInCny, 'CNY');
        }
        return format(converted, currency);
      },
      moneyCny: (v) => format(v === null || v === undefined ? null : v, 'CNY'),
      unavailable: currency === 'BRL' && rate === null,
    }),
    [currency, rate, convert, format],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency precisa estar dentro de <CurrencyProvider>');
  return ctx;
}
